'use client';

import { useEffect, useMemo, useRef, useState, type ComponentProps } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Button, Callout, MultiSelect, Spinner } from '@galapagos/design-system/web';
import type { SignupProgramOption } from '@galapagos/shared/registration';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { PrivacyNote } from '../components/PrivacyNote';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { hasSession } from '@/services/session';
import { useSignupApi } from '../hooks/useSignupApi';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useSignupProgramOptions } from '../hooks/useSignupProgramOptions';
import { useCatalogWords } from '../hooks/useCatalogWords';
import { useSignupAllowedProgramKeys } from '../hooks/useSignupAllowedProgramKeys';
import { useStepHeading } from '../hooks/useStepHeading';
import {
  allowedProgramOptions,
  isCompleteProgramSelection,
  keptProgramSelection,
  programFloorOutcome,
  selectedProgramNotes,
  wordedProgramOptions,
  type WordedProgramOption,
} from '../services/program-options';
import { nextSignupStep } from '../steps';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { ReturnToStep } from './ReturnToStep';
import { SingleProgramChoice } from './SingleProgramChoice';
import { EarlierAnswersGate } from './EarlierAnswersGate';

type SelectOptions = ComponentProps<typeof MultiSelect>['options'];

function toSelectOptions(
  options: readonly WordedProgramOption[],
  infoDotLabel: (option: string) => string,
): SelectOptions {
  return options.map((option) => ({
    value: option.programKey,
    label: option.label,
    ...(option.description
      ? {
          description: option.description,
          descriptionReveal: 'info-dot' as const,
          infoDotAccessibilityLabel: infoDotLabel(option.label),
        }
      : {}),
    ...(option.subOptions?.length
      ? { subOptions: option.subOptions, subOptionsLabel: option.subOptionsLabel }
      : {}),
  }));
}

function ProgramChoice({
  options,
  repick,
  onContinue,
}: {
  options: readonly SignupProgramOption[];
  /** Choosing again after every earlier choice was too young: start empty. */
  repick: boolean;
  onContinue: (selection: string[]) => void;
}) {
  const { t } = useTranslation('signup');
  const storedSelection = useSignupFlowStore((state) => state.programSelection);
  const stored = repick ? null : storedSelection;
  const [selection, setSelection] = useState(() => keptProgramSelection(options, stored));
  const words = useCatalogWords();
  const worded = useMemo(() => wordedProgramOptions(words, options), [words, options]);
  const selectOptions = useMemo(
    () => toSelectOptions(worded, (option) => t('substance.infoDotLabel', { option })),
    [worded, t],
  );
  const complete = isCompleteProgramSelection(options, selection);
  return (
    <>
      {/* The choices are health information: ph-no-capture keeps PostHog's
          autocapture (label text, test ids, rage clicks) away from them, as
          the onboarding flow host does for its answers. */}
      <div className="signup-step__group ph-no-capture" data-testid="signup-programs-capture-guard">
        <MultiSelect
          actionId="signup.substance-screen-program.change"
          infoDotActionId="signup.substance-screen-program-info.toggle"
          label={t('substance.optionsLabel')}
          hideLabel
          options={selectOptions}
          value={selection}
          onSelect={setSelection}
          data-testid="signup-programs"
        />
        {selectedProgramNotes(worded, selection).map((note) => (
          <p key={note} className="signup-selected-note">
            {note}
          </p>
        ))}
      </div>
      <PrivacyNote text={t('substance.privacyNote')} />
      <FormActions>
        <Button
          size="L"
          type="Primary"
          label={t('actions.continue')}
          state={complete ? 'Default' : 'Disabled'}
          onPress={() => onContinue(selection)}
          actionId="signup.substance-screen-continue.activate"
          data-testid="signup-continue"
        />
      </FormActions>
    </>
  );
}

function SubstanceStep() {
  const allowed = useSignupAllowedProgramKeys();
  const programOptions = useSignupProgramOptions();
  const { goToStep, goToNextStep, goToErrorScreen, path } = useSignupNavigation();
  const setProgramSelection = useSignupFlowStore((state) => state.setProgramSelection);
  const storedExcluded = useSignupFlowStore((state) => state.programRepickExcluded);
  const setProgramRepickExcluded = useSignupFlowStore((state) => state.setProgramRepickExcluded);
  // The choice made again after the profile save is never stored, so a
  // reload during it reads the save's answer back: a signed-in return visit
  // with a kept choice asks the profile which programs the member's age rules
  // out, as the save did.
  const api = useSignupApi();
  const scenario = useSearchParams().get('scenario');
  const storedSelection = useSignupFlowStore((state) => state.programSelection);
  const rereadRepick = storedExcluded === null && storedSelection !== null && hasSession();
  const savedProfile = useQuery({
    // Its own key: an answer read here before the save must never stand in
    // for the wrap-up's read after it.
    queryKey: ['signup', 'registration-profile', 'repick', scenario],
    queryFn: () => api.getRegistrationProfile(),
    enabled: rereadRepick,
    retry: false,
  });
  // Decided while rendering, so the list never shows a frame with the
  // rejected choice still in it; the store learns it after.
  const recoveredExcluded =
    rereadRepick &&
    storedSelection !== null &&
    savedProfile.data !== undefined &&
    savedProfile.data.ineligibleProgramKeys.length > 0 &&
    programOptions.data !== undefined &&
    programFloorOutcome(
      programOptions.data.options,
      storedSelection,
      savedProfile.data.ineligibleProgramKeys,
    ).kind === 'repick'
      ? savedProfile.data.ineligibleProgramKeys
      : null;
  useEffect(() => {
    if (recoveredExcluded !== null) setProgramRepickExcluded(recoveredExcluded);
  }, [recoveredExcluded, setProgramRepickExcluded]);
  const repickExcluded = storedExcluded ?? recoveredExcluded;
  const repick = repickExcluded !== null;
  const { t } = useTranslation('signup');
  const title = t('substance.title');
  const headingRef = useStepHeading(title);
  ensureSignupStepStyle();

  // Production sends a failed client config lookup, whatever the error, to its
  // error screen with Book a call; the sponsor and the program options are the
  // two halves of that lookup here.
  const failed = allowed.kind === 'failed' || programOptions.isError;
  const left = useRef(false);
  useEffect(() => {
    if (!failed || left.current) return;
    left.current = true;
    goToErrorScreen();
  }, [failed, goToErrorScreen]);

  const loading =
    !failed &&
    (allowed.kind === 'loading' ||
      programOptions.isPending ||
      (rereadRepick && savedProfile.isLoading));
  // While the save's answer is read back, no list shows: one shown now could
  // keep a choice the age rules out.
  // A sponsor that allows none of the options gets an empty list, as in
  // production; it is not a failed call. No keys at all means no restriction.
  const offered =
    allowed.kind === 'ready' && programOptions.data && !(rereadRepick && savedProfile.isLoading)
      ? allowed.programKeys === null
        ? programOptions.data.options
        : allowedProgramOptions(programOptions.data.options, allowed.programKeys)
      : null;
  // Choosing again after the profile save: the programs above the member's
  // age leave the list, and when none is left the member is told so.
  const offeredNow =
    offered && repick
      ? offered.filter((option) => !repickExcluded.includes(option.programKey))
      : offered;
  const noneAvailable = repick && offeredNow !== null && offeredNow.length === 0;
  // The sponsor's rule: one program at a time, or several.
  const singleChoice = allowed.kind === 'ready' && !allowed.multipleSelection;
  const Choice = singleChoice ? SingleProgramChoice : ProgramChoice;

  // A link without an employer whose check result a reload dropped runs the
  // check again (a match comes back here, no match goes to the search).
  if (allowed.kind === 'recheck') return <ReturnToStep step="eligibility-check" />;

  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} headingRef={headingRef} />
        <Callout
          tone="info"
          title={singleChoice ? t('substance.calloutTitleSingle') : t('substance.calloutTitle')}
          body={singleChoice ? t('substance.calloutBodySingle') : t('substance.calloutBody')}
        />
        {/* The one status announcer for this step: always mounted, its text
            changes. The message shown when no program is open to the member
            is hidden from assistive technology so it is not announced twice. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status">
          {loading ? t('substance.loading') : null}
          {noneAvailable ? (
            <>
              <span>{t('substance.noneAvailableTitle')}</span>{' '}
              <span>{t('substance.noneAvailableBody')}</span>
            </>
          ) : null}
        </p>
        {loading ? <Spinner accessibilityLabel={t('substance.loading')} /> : null}
        {noneAvailable ? (
          <div aria-hidden="true">
            <Callout
              tone="info"
              title={t('substance.noneAvailableTitle')}
              body={t('substance.noneAvailableBody')}
              data-testid="signup-programs-none-available"
            />
          </div>
        ) : null}
        {repick && offeredNow && !noneAvailable ? (
          <Callout
            tone="info"
            title={t('substance.repickTitle')}
            body={t('substance.repickBody')}
            data-testid="signup-programs-repick"
          />
        ) : null}
        {offeredNow && !noneAvailable ? (
          <Choice
            // A new choice list when the step turns into choosing again, so
            // nothing picked before carries into it.
            key={repick ? 'repick' : 'pick'}
            options={offeredNow}
            repick={repick}
            onContinue={(selection) => {
              setProgramSelection(selection);
              if (!repick) {
                goToNextStep('substance');
                return;
              }
              // A second choice made after the profile save picks up where
              // that save left off, not at the steps already done.
              setProgramRepickExcluded(null);
              const afterContact = nextSignupStep('contact', path);
              if (afterContact !== null) goToStep(afterContact);
            }}
          />
        ) : (
          <FormActions>
            <Button
              size="L"
              type="Primary"
              label={t('actions.continue')}
              state="Disabled"
              actionId="signup.substance-screen-continue.activate"
              data-testid="signup-continue"
            />
          </FormActions>
        )}
      </div>
    </SignupShell>
  );
}

/** `/substance/`: what the member wants to focus on (v9 1.2 and 1.2a). */
export function SubstanceStepScreen() {
  return (
    <EarlierAnswersGate step="substance">
      <SubstanceStep />
    </EarlierAnswersGate>
  );
}
